import React from "react";
import { Input, Select, Checkbox, Textarea } from "@chakra-ui/react";
import { Select as ChakraSelect } from "chakra-react-select";

const chakraStyles = {
  container: (provided: any) => ({
    ...provided,
    width: "100%",
  }),
  control: (provided: any) => ({
    ...provided,
    fontSize: "xs",
    minH: 8,
  }),
  valueContainer: (provided: any) => ({
    ...provided,
    py: 0,
  }),
  input: (provided: any) => ({
    ...provided,
  }),
  option: (provided: any) => ({
    ...provided,
    fontSize: "xs",
  }),
  multiValueLabel: (provided: any) => ({
    ...provided,
    fontSize: "xs",
  }),
};

interface FieldInputProps {
  fieldType: string;
  value: any;
  onChange: (value: any) => void;

  // String attributes
  stringAllowedValues?: string;

  // Number attributes
  decimalPlaces?: number | "";
  numberAllowedValues?: string;

  // Date attributes
  dateMinValue?: string;
  dateMaxValue?: string;

  // Time attributes
  timeMinValue?: string;
  timeMaxValue?: string;

  // Multi-select value (for TextMultiSelection and NumberMultiSelection)
  multiSelectValue?: Array<{ value: string; label: string }>;
  onMultiSelectChange?: (
    value: Array<{ value: string; label: string }>
  ) => void;

  // Optional styling
  size?: string;
  bg?: string;
}

export const FieldInput: React.FC<FieldInputProps> = ({
  fieldType,
  value,
  onChange,
  stringAllowedValues,
  decimalPlaces,
  numberAllowedValues,
  dateMinValue,
  dateMaxValue,
  timeMinValue,
  timeMaxValue,
  multiSelectValue,
  onMultiSelectChange,
  size = "sm",
  bg = "white",
}) => {
  const isMultiSelect =
    fieldType === "TextMultiSelection" || fieldType === "NumberMultiSelection";

  const stringOptions = stringAllowedValues
    ? stringAllowedValues.split(",").map((v) => v.trim())
    : [];

  const numberOptions = numberAllowedValues
    ? numberAllowedValues.split(",").map((v) => v.trim())
    : [];

  const stringSelectOptions = stringOptions.map((option) => ({
    value: option,
    label: option,
  }));

  const numberSelectOptions = numberOptions.map((option) => ({
    value: option,
    label: option,
  }));

  // Text Input
  if (fieldType === "Text") {
    return (
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Enter value..."
        size={size}
        bg={bg}
      />
    );
  }

  // Text Area
  if (fieldType === "TextArea") {
    return (
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Enter value..."
        size={size}
        bg={bg}
        rows={4}
      />
    );
  }

  // Text Selection (Dropdown)
  if (fieldType === "TextSelection") {
    return (
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Select option..."
        size={size}
        bg={bg}
        borderRadius="md"
      >
        {stringOptions.map((option, index) => (
          <option key={index} value={option}>
            {option}
          </option>
        ))}
      </Select>
    );
  }

  // Text Multi-Selection
  if (fieldType === "TextMultiSelection") {
    return (
      <ChakraSelect
        isMulti
        value={multiSelectValue}
        onChange={(selected) =>
          onMultiSelectChange?.(
            selected as Array<{ value: string; label: string }>
          )
        }
        options={stringSelectOptions}
        placeholder="Select options..."
        size={size as "sm" | "md" | "lg"}
        chakraStyles={chakraStyles}
      />
    );
  }

  // Number Input
  if (fieldType === "Number") {
    return (
      <Input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Enter value..."
        size={size}
        bg={bg}
        step={decimalPlaces ? Math.pow(10, -Number(decimalPlaces)) : 1}
      />
    );
  }

  // Number Selection
  if (fieldType === "NumberSelection") {
    return (
      <Select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Select option..."
        size={size}
        bg={bg}
        borderRadius="md"
      >
        {numberOptions.map((option, index) => (
          <option key={index} value={option}>
            {option}
          </option>
        ))}
      </Select>
    );
  }

  // Number Multi-Selection
  if (fieldType === "NumberMultiSelection") {
    return (
      <ChakraSelect
        isMulti
        value={multiSelectValue}
        onChange={(selected) =>
          onMultiSelectChange?.(
            selected as Array<{ value: string; label: string }>
          )
        }
        options={numberSelectOptions}
        placeholder="Select options..."
        size={size as "sm" | "md" | "lg"}
        chakraStyles={chakraStyles}
      />
    );
  }

  // Date
  if (fieldType === "Date") {
    return (
      <Input
        type="date"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        min={dateMinValue || undefined}
        max={dateMaxValue || undefined}
        size={size}
        bg={bg}
        borderRadius="md"
      />
    );
  }

  // DateTime
  if (fieldType === "DateTime") {
    return (
      <Input
        type="datetime-local"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        min={dateMinValue || undefined}
        max={dateMaxValue || undefined}
        size={size}
        bg={bg}
        borderRadius="md"
      />
    );
  }

  // Time
  if (fieldType === "Time") {
    return (
      <Input
        type="time"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        min={timeMinValue || undefined}
        max={timeMaxValue || undefined}
        size={size}
        bg={bg}
        borderRadius="md"
      />
    );
  }

  // Toggle (Checkbox)
  if (fieldType === "Toggle") {
    return (
      <Checkbox
        isChecked={value}
        onChange={(e) => onChange(e.target.checked)}
      />
    );
  }

  return null;
};
